import React from 'react';
import {CatalogItemVO, CiselnikVO, LayerAttributeEnumVO, LayerVO} from '../api';
import {LayerId} from '@mc/react-mapio';
import {sortCiselniky} from './Formatter';

export const booleanToBinary = obj => {
    for (let key in obj) {
        if (typeof obj[key] === 'boolean') {
            obj[key] = obj[key] ? 1 : 0;
        }
    }
    return obj;
};

export const binaryToBoolean = obj => {
    for (let key in obj) {
        if (obj[key] === 1 || obj[key] === 0) {
            obj[key] = Boolean(obj[key]);
        }
    }
    return obj;
};

export const booleanToString = obj => {
    for (let key in obj) {
        if (typeof obj[key] === 'boolean') {
            obj[key] = obj[key] ? 'true' : 'false';
        }
    }
    return obj;
};

/***
 * mame nekde objekty a chceme posilat idcka... z nejakeho duvodu nepouzivame normalni VF s useIdAsValue
 * @param obj
 */
export const simplifyProperties = obj => {
    for (let key in obj) {
        obj[key] = obj[key]?.id || obj[key];
    }
    return obj;
};

export const removeUncontained = (obj, submitData) => {
    for (let key in obj) {
        if (submitData[key] === undefined || submitData[key] === null) {
            obj[key] = null;
        }
    }
    return obj;
};

export const nestedSort =
    (prop1, prop2 = null, direction = 'asc') =>
    (e1, e2) => {
        const a = prop2 ? e1[prop1][prop2] : e1[prop1],
            b = prop2 ? e2[prop1][prop2] : e2[prop1],
            sortOrder = direction === 'asc' ? 1 : -1;
        return a < b ? -sortOrder : a > b ? sortOrder : 0;
    };

export const removeSpaces = (string: string) => {
    return string.replace(/\s+/g, '');
};

export const findNestedLabelById = (objects, targetId) => {
    let foundLabel = null;
    objects?.forEach(object => {
        // Pokud hledáme 'label' vrstvy s diskiminátorem, tak to 'id' obsahuje i ono 'targetId'
        if (object.layerId == targetId || object.id == targetId || (!object.layerId && object.id === targetId)) {
            foundLabel = object.label;
        } else if (object.children && object.children.length > 0) {
            const nestedLabel = findNestedLabelById(object.children, targetId);
            if (nestedLabel) {
                foundLabel = nestedLabel;
            }
        }
    });
    return foundLabel;
};

/** Vrátí z objektu číselníku pouze id */
export const flattenEnumObject = (properties: any): any => {
    const processedProperties = {};
    for (const key in properties) {
        if (properties[key] && typeof properties[key] === 'object' && properties[key].id) {
            processedProperties[key] = properties[key].id;
        } else {
            processedProperties[key] = properties[key];
        }
    }
    return processedProperties;
};

/** Zkrátí text o daný limit pokud jej přesahuje a přidá na konce "..." */
export const truncate = (string: string, limit: number) => {
    if (!string || string.length <= limit) {
        return string;
    }
    return string.slice(0, limit) + '...';
};

/**
 * Najde vrstvu v katalogu podle layerId
 * @param data - data katalogu
 * @param currentLayerId - ID vrstvy jako číslo
 * @returns true pokud vrstva existuje v katalogu
 */
export const findLayer = (data: CatalogItemVO[], currentLayerId: LayerId): boolean => {
    for (const item of data) {
        if (!item) continue;

        // Převedeme layerId a id na čísla pro porovnání
        const itemLayerId = typeof item.layerId === 'number' ? item.layerId : Number(item.layerId);
        const itemId = typeof item.id === 'number' ? item.id : Number(item.id);

        if (itemLayerId === currentLayerId || itemId === currentLayerId) {
            return true;
        }

        // Rekurzivně prohledáváme potomky
        if (item.children && item.children.length > 0) {
            if (findLayer(item.children, currentLayerId)) {
                return true;
            }
        }
    }
    return false;
};

/**
 * Zkontroluje zda vrstva má aktivní potomky
 * @param data - data katalogu
 * @param currentLayerId - ID vrstvy jako číslo
 * @param activeLayers - pole aktivních vrstev jako čísla
 * @returns true pokud má vrstva aktivní potomky
 */
export const hasActiveChildren = (data: CatalogItemVO[], currentLayerId: LayerId, activeLayers: LayerId[]): boolean => {
    for (const item of data) {
        if (!item) continue;

        // Převedeme layerId a id na čísla pro porovnání
        const itemLayerId = typeof item.layerId === 'number' ? item.layerId : Number(item.layerId);
        const itemId = typeof item.id === 'number' ? item.id : Number(item.id);

        if ((itemLayerId === currentLayerId || itemId === currentLayerId) && item.children && item.children.length > 0) {
            // Projdeme všechny potomky a zkontrolujeme zda jsou v aktivních vrstvách
            for (const child of item.children) {
                if (child.layerId) {
                    const childLayerId = typeof child.layerId === 'number' ? child.layerId : Number(child.layerId);
                    if (activeLayers.includes(childLayerId)) {
                        return true;
                    }
                }
            }
        }

        // Rekurzivně prohledáváme potomky
        if (item.children && item.children.length > 0) {
            if (hasActiveChildren(item.children, currentLayerId, activeLayers)) {
                return true;
            }
        } else return true;
    }
    return false;
};

/**
 * Zkontroluje zda vrstva má potomka s diskriminátorem (parentLayerId)
 * @param data - data katalogu
 * @param currentLayerId - ID vrstvy jako číslo
 * @param layersConfig - konfigurace vrstev pro kontrolu parentLayerId
 * @returns true pokud má vrstva potomka s diskriminátorem
 */
export const hasChildWithDiscriminator = (data: CatalogItemVO[], currentLayerId: LayerId, layersConfig?: {[key: string]: LayerVO}): boolean => {
    // Pomocná funkce pro kontrolu zda vrstva má diskriminátor (existuje parentLayerId)
    const hasDiscriminator = (layerId: LayerId): boolean => {
        if (!layersConfig || !layerId) return false;
        const layerConfig = layersConfig[layerId.toString()];
        return Boolean(layerConfig?.parentLayerId);
    };

    for (const item of data) {
        if (!item) continue;

        // Převedeme layerId a id na čísla pro porovnání
        const itemLayerId = typeof item.layerId === 'number' ? item.layerId : Number(item.layerId);
        const itemId = typeof item.id === 'number' ? item.id : Number(item.id);

        // Zkontrolujeme zda se jedná o hledanou vrstvu
        if ((itemLayerId === currentLayerId || itemId === currentLayerId) && item.children && item.children.length > 0) {
            // Zkontrolujeme zda má některý potomek diskriminátor
            for (const child of item.children) {
                if (child.layerId) {
                    const childLayerId = typeof child.layerId === 'number' ? child.layerId : Number(child.layerId);
                    if (hasDiscriminator(childLayerId)) {
                        return true;
                    }
                }
            }
        }

        // Rekurzivně prohledáváme potomky
        if (item.children && item.children.length > 0) {
            if (hasChildWithDiscriminator(item.children, currentLayerId, layersConfig)) {
                return true;
            }
        }
    }
    return false;
};

/** Má číselník atributu stromovou strukturu (alespoň jedna hodnota má nadřazenou hodnotu)? */
export const isCiselnikTree = (ciselniky?: LayerAttributeEnumVO[]): boolean => (ciselniky ?? []).some(c => c.parentId != null);

/**
 * ID hodnot číselníku, které mají alespoň jednoho potomka – ve stromovém číselníku jde o uzly.
 * Uzel slouží pouze k seskupení hodnot, jako hodnotu atributu jej vybrat nelze (MCW-29258).
 */
export const getCiselnikUzelIds = (ciselniky?: LayerAttributeEnumVO[]): Set<number> =>
    new Set((ciselniky ?? []).map(c => c.parentId).filter((id): id is number => id !== undefined && id !== null));

/**
 * Predikát pro `allowSelectItem` stromového výběru hodnot číselníku – vybrat lze jen koncovou
 * hodnotu (list), uzly zůstanou ve výpisu vidět kvůli orientaci, ale nejdou vybrat (MCW-29258).
 */
export const allowSelectCiselnikList = (ciselniky?: LayerAttributeEnumVO[]): ((id: number | string) => boolean) => {
    const uzelIds = getCiselnikUzelIds(ciselniky);
    return id => !uzelIds.has(Number(id));
};

/**
 * Koncové hodnoty (listy) číselníku v konfiguraci atributu, tedy hodnoty, které nejsou nadřazenou
 * hodnotou žádné jiné hodnoty. Konfigurační VO neváže hodnoty přes ID, ale přes tempId /
 * parentTempId (viz {@link CiselnikVO}), proto nejde použít {@link getCiselnikUzelIds}.
 * <p>
 * Vnitřní uzel stromového číselníku slouží jen k seskupení hodnot – hodnotou atributu není, takže
 * ho nelze nabízet ani jako výchozí hodnotu atributu (MCW-29258). U plochého číselníku vrací
 * všechny hodnoty.
 */
export const koncoveHodnotyCiselniku = (ciselniky?: CiselnikVO[]): CiselnikVO[] => {
    const idsUzlu = new Set((ciselniky ?? []).map(c => c.parentTempId).filter(tempId => tempId != null));
    return (ciselniky ?? []).filter(c => !idsUzlu.has(c.tempId));
};

/** Hodnota číselníku s vnořenými potomky pro stromový výběr (VF.SelectBox tree). */
type CiselnikTreeItem = LayerAttributeEnumVO & {children?: CiselnikTreeItem[]};

/**
 * Z plochého seznamu hodnot číselníku sestaví strom (vnořené `children`) podle vazby `parentId`.
 * Hodnoty jsou v rámci každé úrovně seřazeny stejně jako ve zbytku aplikace ({@link sortCiselniky}).
 * Uzly bez potomků nemají pole `children` (aby se nezobrazovala prázdná rozbalovací šipka).
 */
export const buildCiselnikSelectTree = (ciselniky?: LayerAttributeEnumVO[]): CiselnikTreeItem[] => {
    const values = sortCiselniky(ciselniky ?? []) as LayerAttributeEnumVO[];
    const byId = new Map<number, CiselnikTreeItem>();
    values.forEach(v => byId.set(v.id, {...v, children: []}));

    const roots: CiselnikTreeItem[] = [];
    values.forEach(v => {
        const node = byId.get(v.id);
        const parent = v.parentId != null ? byId.get(v.parentId) : undefined;
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    const pruneEmptyChildren = (node: CiselnikTreeItem): void => {
        if (node.children?.length === 0) {
            delete node.children;
        } else {
            node.children?.forEach(pruneEmptyChildren);
        }
    };
    roots.forEach(pruneEmptyChildren);

    return roots;
};

/** Hodnota číselníku s hloubkou zanoření pro odsazené zobrazení v plochém seznamu. */
export type CiselnikFlatTreeItem = LayerAttributeEnumVO & {depth: number};

/**
 * Rozloží strom hodnot číselníku ({@link buildCiselnikSelectTree}) do plochého seznamu v pořadí
 * vykreslení (rodič, pak jeho potomci) a ke každé hodnotě doplní hloubku zanoření.
 * Použije se tam, kde komponenta strom neumí (vícenásobný výběr), ale hierarchii chceme
 * naznačit odsazením hodnot.
 */
export const flattenCiselnikTree = (ciselniky?: LayerAttributeEnumVO[]): CiselnikFlatTreeItem[] => {
    const result: CiselnikFlatTreeItem[] = [];

    const appendLevel = (nodes: CiselnikTreeItem[], depth: number): void => {
        nodes.forEach(({children, ...node}) => {
            result.push({...node, depth});
            if (children && children.length > 0) {
                appendLevel(children, depth + 1);
            }
        });
    };
    appendLevel(buildCiselnikSelectTree(ciselniky), 0);

    return result;
};

/**
 * Odsazené vykreslení hodnoty stromového číselníku ve vícenásobném výběru (VF.MultiSelect strom neumí).
 * Použije se jako `renderItem` s položkami z {@link flattenCiselnikTree}.
 */
export const renderCiselnikTreeItem = (item: {name?: React.ReactNode; depth?: number}): React.ReactNode => (
    <span className={'ciselnik-tree-item'} style={{paddingLeft: (item.depth ?? 0) * 16}}>
        {item.name}
    </span>
);

/** Odsazení jedné úrovně zanoření – nezlomitelné mezery (jinak by je HTML sloučilo do jedné). */
const CISELNIK_TREE_INDENT = '\u00A0\u00A0\u00A0\u00A0';

/**
 * Sestaví položky výběru z hodnot číselníku – u stromového číselníku v pořadí stromu (rodič, pak jeho
 * potomci) a s hierarchií naznačenou odsazením, u plochého číselníku seřazené podle názvu.
 * <p>
 * Odsazení je součástí názvu (ne stylu), protože filtr FinalTable (typ {@code multiSelect}) předává
 * komponentě jen položky – vlastní vykreslení položky (renderItem) nepodporuje. Název musí zůstat
 * textem, jinak přestane fungovat hledání v seznamu hodnot (výchozí getLabel nestringový název
 * ignoruje). Důsledkem je, že odsazení je vidět i v zavřeném filtru u názvu vybrané hodnoty.
 *
 * @param ciselniky - hodnoty číselníku atributu
 * @returns položky výběru (id a název s případným odsazením)
 */
export const buildCiselnikFilterItems = (ciselniky?: LayerAttributeEnumVO[]): {id: number; name: string}[] => {
    if (isCiselnikTree(ciselniky)) {
        return flattenCiselnikTree(ciselniky).map(c => ({
            id: c.id,
            name: CISELNIK_TREE_INDENT.repeat(c.depth) + (c.nazev ?? ''),
        }));
    }
    return (sortCiselniky(ciselniky ?? []) as LayerAttributeEnumVO[]).map(c => ({id: c.id, name: c.nazev ?? ''}));
};
